import React from 'react';
import {
  categoryIds,
  formatMoney,
  T_OrderProduct,
  includedProductServiceCodes,
  useCoupon,
  productSkuIds,
  T_Product,
  useCouponStore,
  useOrderStore,
  T_VC_ProductType,
} from '@sky-tv-group/shared';
import { Bin } from './RunningTotalIcons';
import cx from 'classnames';
import { useState } from 'react';
import { T_Voucher } from '@sky-tv-group/shared/src/types';

interface RunningTotalProductProps {
  product: T_OrderProduct;
  removable?: boolean;
  included?: boolean;
  subscribed?: boolean;
  crossout?: boolean;
  oneOff?: boolean;
  removeHandlerBegin?: (value: boolean) => void;
  removeHandlerEnd?: (value: boolean) => void;
  removeProduct: (product: T_Product) => void;
  oneoffBoxType?: string;
  showoneOff?: boolean;
  showNew?: boolean;
}

const RunningTotalProduct = React.memo(
  ({
    product,
    removable = true,
    included = false,
    crossout = false,
    subscribed,
    removeHandlerBegin,
    removeHandlerEnd,
    oneOff = false,
    removeProduct,
    oneoffBoxType,
    showoneOff = false,
    showNew = false,
  }: RunningTotalProductProps) => {
     const { eligibilityOffers } = useCouponStore();
      const eligibilityExcludeSKus= eligibilityOffers?.eligibilityExcludeServiceCode?.split(',') ?? [];
      const isEligibilityExcludeProduct=eligibilityExcludeSKus?.includes(product?.product?.sku)&&product?.currentlySubscribed;
    const isMultiroomBoxOrBoxOneOffFee =
      product.categoryId === categoryIds.multiroom || product.product.sku === productSkuIds.arrowUpfrontBoxFee.primary;

    const isIncluded = includedProductServiceCodes.includes(product.product.sku);
    const isSkyAccess = product.product.sku === productSkuIds.access.primary;
    //adding bin icon for subscribed products which are include in recontract offer except starter
    const offerProducts = eligibilityOffers?.eligibilityIncludeServiceCode?.split(',').filter(sku=> sku!==productSkuIds.starter.primary)??[];
    const isEligibilityOfferProduct = offerProducts?.includes(product?.product?.sku);
    removable = subscribed ? isEligibilityOfferProduct: removable;

    /** Show striked out original price.  */
    let hasOffer = false;

    if (product.product.categoryId === categoryIds.broadband) {
      hasOffer = (subscribed ? product.product.price0 : product.product.price1) !== product.price;
    } else {
      hasOffer = product.price !== product.product.priceIncTax;
    }

    // only BB plans has price1, which is the price with starter offer
    // For BB products if subscribed, show price0 (original price) else price1
    const normalPrice = hasOffer ? (
      formatMoney(
        ((subscribed ? product.product.price0 : product.product.price1) ?? product.product.priceIncTax) *
          product.quantity
      )
    ) : (
      <span></span>
    );
    const normalPriceClasses = cx('text-secondary-red line-through pr-1', {
      'w-auto': hasOffer,
      'w-full': !hasOffer,
      'sky-h6-reg': showNew,
      'sky-h7-reg': !showNew,
    });
    const { hasDynamicCoupon } = useCoupon();
    const isWifiBooster = product.product.sku === productSkuIds.meshDevice.primary;

    const [removeDisabled, setRemoveDisabled] = useState(false);
    const {voucher, setPromoCodeVoucher, getSplitCoupons, coupons} = useCouponStore();
    const { bbCoupon } = getSplitCoupons(coupons); // Split Voucher Coupons to get BB coupon if exists
    const isVoucherProduct = ((voucher?.productType === T_VC_ProductType.DTH_AND_BROADBAND || voucher?.productType === T_VC_ProductType.BROADBAND) && voucher?.isVoucher && bbCoupon && product.product.categoryId === categoryIds.broadband) ? true : voucher?.skus?.includes(product?.product?.sku)
     // VOUCHER_ATL: When voucher product type is DTH_AND_BROADBAND or BROADBAND and there is a BB coupon in the store and user trying to remove bb product then we are allowing mendetory to remove voucher

    async function removeHandler() {
      if(!isVoucherProduct){
        setRemoveDisabled(true);
        if (removeHandlerBegin) removeHandlerBegin(true);
        await removeProduct(product.product);
        if (removeHandlerEnd) removeHandlerEnd(false);
        setRemoveDisabled(false);
      }else{
        let selectedBBProductId = null
        if (product.product.categoryId === categoryIds.broadband && (voucher?.productType === T_VC_ProductType.BROADBAND || voucher?.productType === T_VC_ProductType.DTH_AND_BROADBAND)) {
          selectedBBProductId = [product.productId]
        }
        setPromoCodeVoucher({...voucher, isRemovalModal:true, selectedBBProductId: selectedBBProductId} as T_Voucher)
      }
    }

    let hasRecording =
      (product.product.sku.includes(productSkuIds.arrowBox.primary) &&
        product.product.sku.includes(productSkuIds.arrowBoxRecording.primary)) ||
      product.product.sku.includes(productSkuIds.upfrontRecording.primary);

    return (
      <div className={`text-primary-mid-night pb-2 ${isEligibilityExcludeProduct?'line-through decoration-secondary-red':''}`}>
        <p
          className={`flex justify-between ${
            showNew ? 'sky-h6-reg items-start' : 'sky-h7-reg text-body-xs items-center'
          } ${crossout ? 'line-through text-secondary-red' : ''}`}
          data-testid={`running-total-product-${product.productId}`}>
          <span className="flex items-center">
            {removable && !removeDisabled && (
              <span
                className="icon-wrapper mr-3 sky-text-primary-pink"
                style={{ width: '15px', height: '15px', display: 'inline-block', cursor: 'pointer' }}>
                <span onClick={removeHandler}>
                  <Bin />
                </span>
              </span>
            )}
            {isMultiroomBoxOrBoxOneOffFee && product.quantity > 1
              ? product.product.name + ' ' + product.quantity + 'x'
              : product.product.name}
            {hasRecording && <small className="text-gray-darker px-2">(My Sky) </small>}
            {isIncluded ? ' (Use included)' : subscribed ? ' (Subscribed)' : ''}
            {/* {oneoffBoxType === 'add' ? ' (add)' : oneoffBoxType === 'upgrade' ? ' (upgrade)' : ''} */}
          </span>

          <span>
            {!isWifiBooster && <span className={normalPriceClasses}>{normalPrice}</span>}
            {formatMoney(isMultiroomBoxOrBoxOneOffFee ? product.price * product.quantity : product.price)}

            {oneOff && !showoneOff ? (
              ''
            ) : oneOff && showoneOff ? (
              <>
                /<br className="md:hidden" />
                <span className="flex justify-end md:inline">one-off</span>
              </>
            ) : (
              '/mth'
            )}
          </span>
        </p>
        <div className="flex justify-end text-gray-darker">
          {product.product.sku.includes(productSkuIds.arrowBox.primary) && product.product.price3 && (
            <p>(Box ${product.product.price3} one-off)</p>
          )}
        </div>

        {(included || isSkyAccess) && (
          <p className="sky-sub pt-1 text-gray-darker ">Included, on a 12 month contract</p>
        )}
        {isSkyAccess && <p className="sky-sub py-1 text-gray-darker ">Includes one My Sky recording capability</p>}
      </div>
    );
  }
);

export { RunningTotalProduct };
